import React from 'react';
import { motion } from 'framer-motion';
import { LayoutGrid, MessageCircle, Users, PenSquare, ArrowRight, TrendingUp, Eye, Star, ShieldCheck, Globe, BookOpen, FileDown, RotateCcw, BarChart3, Navigation, Briefcase } from 'lucide-react';
import { useProjects } from '../../hooks/useProjects';
import { useTestimonials } from '../../hooks/useTestimonials';
import { useClients } from '../../hooks/useClients';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import { useAnalytics, resetAnalytics } from '../../hooks/useAnalytics';
interface StatCardProps {
  label: string;
  count: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  bgColor: string;
  onClick?: () => void;
  delay: number;
  clickable?: boolean;
}
function StatCard({
  label,
  count,
  subtitle,
  icon,
  bgColor,
  onClick,
  delay,
  clickable = true
}: StatCardProps) {
  const Wrapper = clickable ? motion.button : motion.div;
  return <Wrapper initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.35
  }} onClick={clickable ? onClick : undefined} className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-left w-full transition-all ${clickable ? 'hover:border-gray-200 hover:shadow-md group cursor-pointer' : ''}`} data-id="element-157">
      <div className="flex items-start justify-between mb-4" data-id="element-158">
        <div className={`w-11 h-11 rounded-xl ${bgColor} flex items-center justify-center`} data-id="element-159">
          {icon}
        </div>
        {clickable && <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" data-id="element-160" />}
      </div>
      <p className="text-3xl font-bold text-[#0C0C0C] mb-1" data-id="element-161">{count}</p>
      <p className="text-sm font-medium text-gray-700" data-id="element-162">{label}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1" data-id="element-163">{subtitle}</p>}
    </Wrapper>;
}
// Mini bar chart for visit history
function MiniBarChart({
  data
}: {
  data: {
    date: string;
    count: number;
  }[];
}) {
  const max = Math.max(...data.map(d => d.count), 1);
  return <div className="flex items-end gap-1.5 h-16" data-id="element-164">
      {data.map((d, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-165">
          <div className="w-full bg-[#00316B]/15 rounded-sm min-h-[2px] transition-all" style={{
        height: `${d.count / max * 100}%`
      }} data-id="element-166">
            <div className="w-full bg-[#00316B] rounded-sm min-h-[2px]" style={{
          height: '100%'
        }} data-id="element-167" />
          </div>
          <span className="text-[9px] text-gray-400 leading-none" data-id="element-168">
            {d.date}
          </span>
        </div>)}
    </div>;
}
interface DashboardTabProps {
  onNavigate: (tab: 'projects' | 'testimonials' | 'clients' | 'blog' | 'services') => void;
}
export function DashboardTab({
  onNavigate
}: DashboardTabProps) {
  const {
    allProjects,
    projects
  } = useProjects();
  const {
    testimonials
  } = useTestimonials();
  const {
    clients
  } = useClients();
  const {
    allPosts,
    posts
  } = useBlogPosts();
  const {
    analytics,
    recentVisits,
    topBlogPosts,
    trafficSources
  } = useAnalytics();
  const publishedProjects = projects.length;
  const draftProjects = allProjects.length - publishedProjects;
  const verifiedTestimonials = testimonials.filter(t => t.verified).length;
  const totalBlogPosts = allPosts.length;
  const publishedBlogPosts = posts.length;
  const adminBlogPosts = allPosts.length;
  const handleResetAnalytics = () => {
    if (window.confirm('Reset all analytics data? This cannot be undone.')) {
      resetAnalytics();
    }
  };
  return <div className="space-y-8" data-id="element-169">
      {/* Welcome */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-170">
        <h2 className="text-2xl font-bold text-[#0C0C0C] mb-1" data-id="element-171">
          Welcome back 👋
        </h2>
        <p className="text-gray-500 text-sm" data-id="element-172">
          Here's an overview of your site activity and content.
        </p>
      </motion.div>

      {/* Analytics Cards */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.05,
      duration: 0.3
    }} data-id="element-173">
        <div className="flex items-center justify-between mb-4" data-id="element-174">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-175">
            <BarChart3 className="w-4 h-4" data-id="element-176" />
            Site Analytics
          </h3>
          <button onClick={handleResetAnalytics} className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors" title="Reset analytics" data-id="element-177">
            <RotateCcw className="w-3 h-3" data-id="element-178" />
            Reset
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-179">
          <StatCard label="Site Visits" count={analytics.siteVisits} subtitle="Unique sessions" icon={<Globe className="w-5 h-5 text-[#00316B]" data-id="element-181" />} bgColor="bg-[#00316B]/10" delay={0.05} clickable={false} data-id="element-180" />
          <StatCard label="Blog Views" count={analytics.blogViews} subtitle="Blog page visits" icon={<Eye className="w-5 h-5 text-[#FF6600]" data-id="element-183" />} bgColor="bg-[#FF6600]/10" delay={0.1} clickable={false} data-id="element-182" />
          <StatCard label="Blog Reads" count={analytics.blogReads} subtitle="Article opens" icon={<BookOpen className="w-5 h-5 text-emerald-600" data-id="element-185" />} bgColor="bg-emerald-50" delay={0.15} clickable={false} data-id="element-184" />
          <StatCard label="Profile Downloads" count={analytics.profileDownloads} subtitle="Company profile" icon={<FileDown className="w-5 h-5 text-violet-600" data-id="element-187" />} bgColor="bg-violet-50" delay={0.2} clickable={false} data-id="element-186" />
        </div>
      </motion.div>

      {/* Visit History Chart */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.3
    }} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-188">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-189">
          Visits — Last 7 Days
        </h3>
        {recentVisits.every(d => d.count === 0) ? <div className="text-center py-6 text-gray-400 text-sm" data-id="element-190">
            No visit data yet. Visits are tracked per session.
          </div> : <MiniBarChart data={recentVisits} data-id="element-191" />}
      </motion.div>

      {/* Traffic Sources */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.22,
      duration: 0.3
    }} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-192">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between" data-id="element-193">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-194">
            <Navigation className="w-3.5 h-3.5" data-id="element-195" />
            Traffic Sources
          </h3>
          <span className="text-xs text-gray-400" data-id="element-196">
            {analytics.siteVisits} total visits
          </span>
        </div>
        {trafficSources.length === 0 ? <div className="px-6 py-10 text-center text-gray-400 text-sm" data-id="element-197">
            No traffic source data yet. Sources are detected on each new
            session.
          </div> : <div className="divide-y divide-gray-50" data-id="element-198">
            {trafficSources.map(({
          source,
          count
        }) => {
          const percentage = analytics.siteVisits > 0 ? Math.round(count / analytics.siteVisits * 100) : 0;
          return <div key={source} className="px-6 py-3.5 flex items-center justify-between" data-id="element-199">
                  <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-200">
                    <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 shrink-0" data-id="element-201">
                      {source.charAt(0)}
                    </div>
                    <span className="text-sm text-gray-700 truncate" data-id="element-202">
                      {source}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-3" data-id="element-203">
                    <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden hidden sm:block" data-id="element-204">
                      <div className="h-full bg-[#00316B] rounded-full transition-all" style={{
                  width: `${Math.max(percentage, 3)}%`
                }} data-id="element-205" />
                    </div>
                    <span className="text-xs text-gray-400 w-10 text-right" data-id="element-206">
                      {percentage}%
                    </span>
                    <span className="text-sm font-bold text-[#0C0C0C] w-8 text-right" data-id="element-207">
                      {count}
                    </span>
                  </div>
                </div>;
        })}
          </div>}
      </motion.div>

      {/* Content Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-208">
        <StatCard label="Projects" count={allProjects.length} subtitle={draftProjects > 0 ? `${publishedProjects} published · ${draftProjects} draft` : `${publishedProjects} published`} icon={<LayoutGrid className="w-5 h-5 text-[#00316B]" data-id="element-210" />} bgColor="bg-[#00316B]/10" onClick={() => onNavigate('projects')} delay={0.25} data-id="element-209" />
        <StatCard label="Testimonials" count={testimonials.length} subtitle={verifiedTestimonials > 0 ? `${verifiedTestimonials} verified` : 'None verified yet'} icon={<MessageCircle className="w-5 h-5 text-[#FF6600]" data-id="element-212" />} bgColor="bg-[#FF6600]/10" onClick={() => onNavigate('testimonials')} delay={0.3} data-id="element-211" />
        <StatCard label="Clients" count={clients.length} subtitle="Logos on trust strip" icon={<Users className="w-5 h-5 text-emerald-600" data-id="element-214" />} bgColor="bg-emerald-50" onClick={() => onNavigate('clients')} delay={0.35} data-id="element-213" />
        <StatCard label="Blog Posts" count={totalBlogPosts} subtitle={`${publishedBlogPosts} published`} icon={<PenSquare className="w-5 h-5 text-violet-600" data-id="element-216" />} bgColor="bg-violet-50" onClick={() => onNavigate('blog')} delay={0.4} data-id="element-215" />
      </div>

      {/* Two Column: Top Posts + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-217">
        {/* Top Blog Posts */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.3
      }} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-218">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60" data-id="element-219">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-220">
              Most Read Articles
            </h3>
          </div>
          {topBlogPosts.length === 0 ? <div className="px-6 py-10 text-center text-gray-400 text-sm" data-id="element-221">
              No blog reads tracked yet.
            </div> : <div className="divide-y divide-gray-50" data-id="element-222">
              {topBlogPosts.map((post, i) => <div key={post.slug} className="px-6 py-3.5 flex items-center justify-between" data-id="element-223">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-224">
                    <span className="text-xs font-bold text-gray-300 w-5 text-center shrink-0" data-id="element-225">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700 truncate" data-id="element-226">
                      {post.slug.replace(/-/g, ' ')}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-[#0C0C0C] shrink-0 ml-3" data-id="element-227">
                    {post.count}
                  </span>
                </div>)}
            </div>}
        </motion.div>

        {/* Quick Actions */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.45,
        duration: 0.3
      }} data-id="element-228">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-229">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 gap-3" data-id="element-230">
            {[{
            label: 'Add a project',
            desc: 'Showcase new work in your portfolio',
            icon: <LayoutGrid className="w-4 h-4" data-id="element-231" />,
            tab: 'projects' as const
          }, {
            label: 'Write a blog post',
            desc: 'Share insights with your audience',
            icon: <PenSquare className="w-4 h-4" data-id="element-232" />,
            tab: 'blog' as const
          }, {
            label: 'Add a testimonial',
            desc: 'Feature client feedback on the site',
            icon: <MessageCircle className="w-4 h-4" data-id="element-233" />,
            tab: 'testimonials' as const
          }, {
            label: 'Add a client logo',
            desc: 'Update the homepage trust strip',
            icon: <Users className="w-4 h-4" data-id="element-234" />,
            tab: 'clients' as const
          }, {
            label: 'Manage services',
            desc: 'Update your service offerings',
            icon: <Briefcase className="w-4 h-4" data-id="element-235" />,
            tab: 'services' as const
          }].map(action => <button key={action.label} onClick={() => onNavigate(action.tab)} className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm transition-all text-left group" data-id="element-236">
                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 group-hover:bg-[#00316B]/10 group-hover:text-[#00316B] transition-colors shrink-0 mt-0.5" data-id="element-237">
                  {action.icon}
                </div>
                <div className="min-w-0" data-id="element-238">
                  <p className="text-sm font-medium text-gray-800 group-hover:text-[#00316B] transition-colors" data-id="element-239">
                    {action.label}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5" data-id="element-240">{action.desc}</p>
                </div>
              </button>)}
          </div>
        </motion.div>
      </div>

      {/* Content Summary */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5,
      duration: 0.3
    }} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-241">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60" data-id="element-242">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-243">
            Content Summary
          </h3>
        </div>
        <div className="divide-y divide-gray-50" data-id="element-244">
          <div className="px-6 py-4 flex items-center justify-between" data-id="element-245">
            <div className="flex items-center gap-3" data-id="element-246">
              <div className="w-8 h-8 rounded-lg bg-[#00316B]/10 flex items-center justify-center" data-id="element-247">
                <Eye className="w-4 h-4 text-[#00316B]" data-id="element-248" />
              </div>
              <div data-id="element-249">
                <p className="text-sm font-medium text-gray-700" data-id="element-250">
                  Published projects
                </p>
                <p className="text-xs text-gray-400" data-id="element-251">
                  Visible on the Work page
                </p>
              </div>
            </div>
            <span className="text-lg font-bold text-[#0C0C0C]" data-id="element-252">
              {publishedProjects}
            </span>
          </div>

          <div className="px-6 py-4 flex items-center justify-between" data-id="element-253">
            <div className="flex items-center gap-3" data-id="element-254">
              <div className="w-8 h-8 rounded-lg bg-[#FF6600]/10 flex items-center justify-center" data-id="element-255">
                <ShieldCheck className="w-4 h-4 text-[#FF6600]" data-id="element-256" />
              </div>
              <div data-id="element-257">
                <p className="text-sm font-medium text-gray-700" data-id="element-258">
                  Verified testimonials
                </p>
                <p className="text-xs text-gray-400" data-id="element-259">Shown on the homepage</p>
              </div>
            </div>
            <span className="text-lg font-bold text-[#0C0C0C]" data-id="element-260">
              {verifiedTestimonials}
            </span>
          </div>

          <div className="px-6 py-4 flex items-center justify-between" data-id="element-261">
            <div className="flex items-center gap-3" data-id="element-262">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center" data-id="element-263">
                <TrendingUp className="w-4 h-4 text-emerald-600" data-id="element-264" />
              </div>
              <div data-id="element-265">
                <p className="text-sm font-medium text-gray-700" data-id="element-266">
                  Total blog articles
                </p>
                <p className="text-xs text-gray-400" data-id="element-267">
                  {publishedBlogPosts} published
                </p>
              </div>
            </div>
            <span className="text-lg font-bold text-[#0C0C0C]" data-id="element-268">
              {totalBlogPosts}
            </span>
          </div>

          <div className="px-6 py-4 flex items-center justify-between" data-id="element-269">
            <div className="flex items-center gap-3" data-id="element-270">
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center" data-id="element-271">
                <Star className="w-4 h-4 text-violet-600" data-id="element-272" />
              </div>
              <div data-id="element-273">
                <p className="text-sm font-medium text-gray-700" data-id="element-274">
                  Average testimonial rating
                </p>
                <p className="text-xs text-gray-400" data-id="element-275">Across all testimonials</p>
              </div>
            </div>
            <span className="text-lg font-bold text-[#0C0C0C]" data-id="element-276">
              {testimonials.length > 0 ? (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1) : '—'}
            </span>
          </div>
        </div>
      </motion.div>

      <p className="text-center text-xs text-gray-400" data-id="element-277">
        All data is stored locally in your browser. Analytics track activity on
        this device only.
      </p>
    </div>;
}